/**
 * Copyright (c) Meta Platforms, Inc. and affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 *
 */

import type {
    DOMConversionMap,
    DOMConversionOutput,
    DOMExportOutput,
    EditorConfig,
    LexicalNode,
    NodeKey,
    SerializedLexicalNode,
    Spread
} from "lexical";

import { $applyNodeReplacement, DecoratorNode } from "lexical";
import * as React from "react";
import { Suspense } from "react";

const ImageComponent = React.lazy(
    // @ts-ignore
    () => import("./ImageComponent")
);

export interface ImagePayload {
    text: string;
    src: string;
    key?: NodeKey;
}

function convertImageElement(domNode: Node): null | DOMConversionOutput {
    if (domNode instanceof HTMLImageElement) {
        const { alt: text, src } = domNode;
        const node = $createImageNode({ text, src });
        return { node };
    }
    return null;
}

export type SerializedImageNode = Spread<
    {
        text: string;
        src: string;
    },
    SerializedLexicalNode
>;

export class ImageNode extends DecoratorNode<JSX.Element> {
    __text: string;
    __src: string;

    static getType(): string {
        return "image";
    }

    static clone(node: ImageNode): ImageNode {
        return new ImageNode(node.__text, node.__src, node.__key);
    }

    static importJSON(serializedNode: SerializedImageNode): ImageNode {
        const { text, src } = serializedNode;
        const node = $createImageNode({
            text,
            src
        });
        return node;
    }

    exportDOM(): DOMExportOutput {
        const element = document.createElement("img");
        element.setAttribute("src", this.__src);
        element.setAttribute("alt", this.__text);
        return { element };
    }

    static importDOM(): DOMConversionMap | null {
        return {
            img: () => ({
                conversion: convertImageElement,
                priority: 0
            })
        };
    }

    constructor(text: string, src: string, key?: NodeKey) {
        super(key);
        this.__text = text;
        this.__src = src;
    }

    exportJSON(): SerializedImageNode {
        return {
            text: this.getText(),
            src: this.getSrc(),
            type: "image",
            version: 1
        };
    }

    // View

    createDOM(config: EditorConfig): HTMLElement {
        const span = document.createElement("span");
        span.className = "editor-image";
        return span;
    }

    updateDOM(): false {
        return false;
    }

    getSrc(): string {
        return this.__src;
    }

    getText(): string {
        return this.__text;
    }

    getTextContent(): string {
        return "![](" + this.__src + ")";
    }

    decorate(): JSX.Element {
        return (
            <Suspense fallback={null}>
                <ImageComponent text={this.__text} src={this.__src} nodeKey={this.getKey()} />
            </Suspense>
        );
    }
}

export function $createImageNode({ text, src, key }: ImagePayload): ImageNode {
    return $applyNodeReplacement(new ImageNode(text, src, key));
}

export function $isImageNode(node: LexicalNode | null | undefined): node is ImageNode {
    return node instanceof ImageNode;
}
